import { useRef, useState } from "react";
import { toast } from "sonner";
import GameShell from "@/components/GameShell";
import BetControls from "@/components/BetControls";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";

const PRESET_MULTS = {
  low: { 16: [16, 9, 2, 1.4, 1.4, 1.2, 1.1, 1.0, 0.5, 1.0, 1.1, 1.2, 1.4, 1.4, 2, 9, 16] },
  medium: { 16: [110, 41, 10, 5, 3, 1.5, 1.0, 0.5, 0.3, 0.5, 1.0, 1.5, 3, 5, 10, 41, 110] },
  high: { 16: [1000, 130, 26, 9, 4, 2, 0.2, 0.2, 0.2, 0.2, 0.2, 2, 4, 9, 26, 130, 1000] },
};

export default function Plinko() {
  const { setUser, user } = useAuth();
  const { currency } = useCurrency();
  const [amount, setAmount] = useState(10);
  const [risk, setRisk] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [ball, setBall] = useState(null); // {path, slot, mult}
  const [recent, setRecent] = useState([]);
  const anim = useRef(0);
  const [animIdx, setAnimIdx] = useState(-1);

  const rows = 16;
  const mults = PRESET_MULTS[risk][rows];

  const placeBet = (betAmount, quiet = false) => new Promise(async (resolve) => {
    setBusy(true);
    setBall(null);
    setAnimIdx(-1);
    try {
      const { data } = await api.post("/games/plinko", {
        amount: Number(betAmount),
        currency,
        rows,
        risk,
      });
      const path = data.bet.result.path;
      clearInterval(anim.current);
      let i = 0;
      anim.current = setInterval(() => {
        i++;
        setAnimIdx(i);
        if (i >= path.length) {
          clearInterval(anim.current);
          setBall({ path, slot: data.bet.result.slot, mult: data.bet.multiplier });
          setUser((u) => ({ ...u, balances: data.balances }));
          setRecent((r) => [{ mult: data.bet.multiplier, won: data.bet.profit > 0 }, ...r].slice(0, 10));
          if (!quiet) {
            if (data.bet.profit > 0) toast.success(`×${data.bet.multiplier} · +${formatAmount(data.bet.profit, currency)}`);
            else toast.error(`×${data.bet.multiplier}`);
          }
          setBusy(false);
          resolve(data.bet);
        }
      }, quiet ? 60 : 100);
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Failed");
      resolve(null);
    }
  });

  const play = async () => {
    if (!user) return;
    if (Number(amount) <= 0) return toast.error("Enter a bet");
    await placeBet(amount);
  };

  // Compute ball x position per row for animation preview
  const previewPath = (ball?.path || []).slice(0, animIdx + 1);
  let xOffset = 0;
  previewPath.forEach((p) => (xOffset += p === "R" ? 1 : -1));

  return (
    <GameShell title="Plinko" subtitle="Drop the ball. Let physics write your fate." accent="#00E676"
      aside={
        <BetControls
          amount={amount}
          setAmount={setAmount}
          onPlay={play}
          autoPlaceBet={(a) => placeBet(a, true)}
          busy={busy}
          disabled={!user || busy}
          playLabel="Drop Ball"
          extra={
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Risk</div>
              <div className="grid grid-cols-3 gap-2">
                {["low", "medium", "high"].map((r) => (
                  <button
                    key={r}
                    onClick={() => setRisk(r)}
                    className={`py-2 rounded-lg text-xs uppercase tracking-wider ${
                      risk === r ? "btn-cyan" : "btn-ghost"
                    }`}
                    data-testid={`plinko-risk-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          }
        />
      }
    >
      <div className="h-[460px] flex flex-col items-center justify-between">
        <div className="relative flex-1 w-full flex items-center justify-center">
          <div className="relative" style={{ width: Math.min(500, rows * 26) + "px" }}>
            {/* pegs */}
            {Array.from({ length: rows }).map((_, r) => (
              <div key={r} className="flex justify-center gap-2 py-1">
                {Array.from({ length: r + 1 }).map((_, c) => {
                  const highlight = previewPath.length > r && c === previewPath.slice(0, r + 1).filter((p) => p === "R").length;
                  return (
                    <div
                      key={c}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background: highlight ? "#FFC107" : "rgba(255,255,255,0.25)",
                        boxShadow: highlight ? "0 0 10px rgba(255,193,7,0.8)" : "none",
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="w-full grid gap-1 mt-2" style={{ gridTemplateColumns: `repeat(${mults.length}, minmax(0, 1fr))` }}>
          {mults.map((m, i) => (
            <div
              key={i}
              className={`text-center text-[10px] font-mono-num py-2 rounded-md border ${
                ball?.slot === i ? "border-amber-500 bg-amber-500/10 text-amber-400 font-bold" : "border-white/5 text-zinc-500"
              }`}
            >
              ×{m}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-3">
        {recent.map((r, i) => (
          <span key={i} className={`chip font-mono-num ${r.won ? "text-emerald-400 border-emerald-500/30" : "text-rose-400 border-rose-500/30"}`}>
            ×{r.mult}
          </span>
        ))}
      </div>
    </GameShell>
  );
}
